/**
 * The site-wide compact toolbar: icon buttons with a short label underneath and a hover title.
 * Used by the Analyse toolbar, the Composer's top toolbar and each slot's tool row.
 */
import { Icon, Spark } from './icons';

export function Toolbar({ children, dense }: { children: React.ReactNode; dense?: boolean }) {
  return <div className={`toolbar${dense ? ' dense' : ''}`} role="toolbar">{children}</div>;
}

export function Tool({ icon, glyph, label, title, onClick, disabled, active, danger, sparkle, accent, toggle, pressed, subtle }: {
  icon?: string;
  /** A drawing of its own in place of the stroke icon (a platform's mark). */
  glyph?: React.ReactNode;
  label?: string;
  title: string;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  active?: boolean;
  danger?: boolean;
  sparkle?: boolean;
  /** The icon alone in the accent colour; the deliberate single splash of colour in a toolbar. */
  accent?: boolean;
  /** A desk toggle (Trim clip, Level): on, a rounded square in its own colour sits behind the icon. */
  toggle?: 'trim' | 'level';
  pressed?: boolean;
  /** Drawn fainter: the small step beside a large one. */
  subtle?: boolean;
}) {
  return (
    <button type="button" className={`tool${active ? ' on' : ''}${danger ? ' danger' : ''}${accent ? ' accent' : ''}${subtle ? ' subtle' : ''}${toggle ? ` toggle toggle-${toggle}${pressed ? ' pressed' : ''}` : ''}`}
      title={title} aria-label={title} aria-pressed={toggle ? Boolean(pressed) : undefined} disabled={disabled} onClick={onClick}>
      {sparkle ? <Spark className="tool-spark" /> : glyph ?? <Icon name={icon ?? 'gear'} />}
      {label && <span className="tool-label">{label}</span>}
    </button>
  );
}

/**
 * A labelled numeric field sized for the toolbar, with up-down arrows. An empty field steps from
 * `base` (the effective default) so the first press lands on a sensible number, not the minimum.
 */
export function ToolField({ label, value, placeholder, min, max, step, base, onChange, width, labelBelow, plain }: {
  label: string;
  value: number | null;
  placeholder: string;
  min: number;
  max: number;
  step?: number;
  /** Where stepping starts from when the field is empty; defaults to min. */
  base?: number;
  onChange: (value: number | null) => void;
  width?: 'narrow' | 'normal';
  /** Label under the field instead of above, lining up with the toolbar's icon labels. */
  labelBelow?: boolean;
  /** No outer stepper arrows; the input's own spinner (shown on hover) is enough. */
  plain?: boolean;
}) {
  const inc = step ?? 1;
  const decimals = inc < 1 ? 1 : 0;
  // Two speeds on decimal fields: the input's own spinner steps by the fine step (0.1s), while these
  // outer arrows step by whole units, so 1.3 to 7.5 is a few clicks, not forty.
  const coarse = Math.max(1, inc);
  const bump = (dir: 1 | -1) => {
    const from = value ?? base ?? min;
    onChange(Number(Math.max(min, Math.min(max, from + dir * coarse)).toFixed(decimals)));
  };
  return (
    <label className={`tool-field${width === 'narrow' ? ' narrow' : ''}`}>
      {!labelBelow && <span className="tool-label">{label}</span>}
      <span className="tool-field-row">
        <input type="number" min={min} max={max} step={inc} value={value ?? ''} placeholder={placeholder} aria-label={label}
          onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)} />
        {!plain && (
          <span className="tool-stepper">
            <button type="button" aria-label={`Increase ${label} by ${coarse}`} title={`+${coarse}`} onClick={() => bump(1)}>
              <svg viewBox="0 0 16 16" aria-hidden="true"><path d="m4 10 4-4 4 4" /></svg>
            </button>
            <button type="button" aria-label={`Decrease ${label} by ${coarse}`} title={`-${coarse}`} onClick={() => bump(-1)}>
              <svg viewBox="0 0 16 16" aria-hidden="true"><path d="m4 6 4 4 4-4" /></svg>
            </button>
          </span>
        )}
      </span>
      {labelBelow && <span className="tool-label">{label}</span>}
    </label>
  );
}

/** Several tools under one label, the label beneath them where a single tool's would sit. */
export function ToolGroup({ label, children, spacing = 'normal' }: {
  label: string;
  children: React.ReactNode;
  /** How far apart its tools sit: `wide` for marks that need air, `tight` for steps that read as pairs. */
  spacing?: 'normal' | 'wide' | 'tight';
}) {
  return (
    <div className={`tool-group tool-group-below${spacing !== 'normal' ? ` tool-group-${spacing}` : ''}`} role="group" aria-label={label}>
      <div className="row tool-group-row">{children}</div>
      <span className="tool-label">{label}</span>
    </div>
  );
}

export function ToolDivider() {
  return <span className="tool-divider" aria-hidden="true" />;
}
